import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { api, type InvoiceListFilters } from '../api/client'
import { useAuth } from '../auth/AuthProvider'
import { useActiveIntegrator } from '../auth/active-integrator'

const STATUSES = [
  'received', 'number_locking', 'persisted', 'pdf_generated',
  'tax_reporting_pending', 'tax_reporting_submitted', 'tax_reporting_confirmed',
  'tax_reporting_failed', 'stored', 'delivering', 'delivered', 'completed',
  'failed', 'storno_requested', 'storno_completed',
]

const TAX_STATUSES = ['PENDING', 'SUBMITTED', 'ACCEPTED', 'REJECTED', 'FAILED']
const PAGE_SIZE = 50

export function InvoicesPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [offset, setOffset] = useState(0)
  const { session } = useAuth()
  const active = useActiveIntegrator()

  // Scope rules:
  //   - non-sysadmin: pinned to session.integratorId; URL hint ignored
  //   - sysadmin + "operating as" override active: pinned to override id
  //   - sysadmin without override: free-form, URL hint honored, blank = all
  const scopedIntegratorId: string | undefined =
    session && !session.isSystemAdmin
      ? active.integratorId
      : active.isOverride
        ? active.integratorId
        : (searchParams.get('integratorId') ?? undefined)

  const canEditIntegratorFilter = !!session?.isSystemAdmin && !active.isOverride

  const filters = useMemo<InvoiceListFilters>(
    () => ({
      integratorId: scopedIntegratorId,
      merchantId: searchParams.get('merchantId') ?? undefined,
      status: searchParams.get('status') ?? undefined,
      taxReportingStatus: searchParams.get('taxReportingStatus') ?? undefined,
      dateFrom: searchParams.get('dateFrom') ?? undefined,
      dateTo: searchParams.get('dateTo') ?? undefined,
      limit: PAGE_SIZE,
      offset,
    }),
    [searchParams, offset, scopedIntegratorId],
  )

  const { data, isLoading, error } = useQuery({
    queryKey: ['invoices', filters],
    queryFn: () => api.invoices.list(filters),
  })

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set(key, value)
    else next.delete(key)
    setSearchParams(next)
    setOffset(0)
  }

  return (
    <div>
      <h1 className="mb-4 text-xl font-bold">Invoices</h1>
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {canEditIntegratorFilter && (
          <FilterInput label="Integrator ID" value={searchParams.get('integratorId') ?? ''} onChange={(v) => setFilter('integratorId', v)} />
        )}
        <FilterInput label="Merchant ID" value={searchParams.get('merchantId') ?? ''} onChange={(v) => setFilter('merchantId', v)} />
        <FilterSelect label="Status" value={searchParams.get('status') ?? ''} onChange={(v) => setFilter('status', v)} options={STATUSES} />
        <FilterSelect label="Tax status" value={searchParams.get('taxReportingStatus') ?? ''} onChange={(v) => setFilter('taxReportingStatus', v)} options={TAX_STATUSES} />
        <FilterInput type="datetime-local" label="Date from" value={searchParams.get('dateFrom') ?? ''} onChange={(v) => setFilter('dateFrom', v)} />
        <FilterInput type="datetime-local" label="Date to" value={searchParams.get('dateTo') ?? ''} onChange={(v) => setFilter('dateTo', v)} />
      </div>

      {isLoading && <div>Loading invoices…</div>}
      {error && <div className="text-red-600">Failed to load: {(error as Error).message}</div>}

      {data && (
        <>
          <div className="mb-2 text-xs text-slate-500">
            {data.total.toLocaleString()} total · showing {offset + 1}–{offset + data.items.length}
          </div>
          <div className="overflow-hidden rounded border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-left text-xs font-semibold uppercase text-slate-600">
                <tr>
                  <th className="px-3 py-2">Number</th>
                  <th className="px-3 py-2">Merchant</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">NAV</th>
                  <th className="px-3 py-2">Created</th>
                  <th className="px-3 py-2 text-right">Attempts</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((inv) => (
                  <tr key={inv.id} className="border-t border-slate-200 hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <Link to={`/invoices/${inv.id}`} className="font-mono text-brand-700 hover:underline">
                        {inv.invoiceNumber ?? inv.id.slice(0, 8)}
                      </Link>
                      {inv.stornoOfInvoiceId && (
                        <StornoBadge originalId={inv.stornoOfInvoiceId} />
                      )}
                    </td>
                    <td className="px-3 py-2">{inv.merchantDisplayName ?? inv.merchantId.slice(0, 8)}</td>
                    <td className="px-3 py-2">
                      <StatusChip status={inv.status} />
                    </td>
                    <td className="px-3 py-2">
                      <TaxStatusChip status={inv.taxReportingStatus} />
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {new Date(inv.createdAt).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-right">{inv.attemptCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex justify-between text-sm">
            <button
              type="button"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              className="rounded border border-slate-300 px-3 py-1 disabled:opacity-40"
            >
              ← previous
            </button>
            <button
              type="button"
              disabled={offset + data.items.length >= data.total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
              className="rounded border border-slate-300 px-3 py-1 disabled:opacity-40"
            >
              next →
            </button>
          </div>
        </>
      )}
    </div>
  )
}

function FilterInput(props: { label: string; value: string; onChange: (v: string) => void; type?: string }) {
  return (
    <label className="text-xs text-slate-600">
      {props.label}
      <input
        type={props.type ?? 'text'}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-sm"
      />
    </label>
  )
}

function FilterSelect(props: { label: string; value: string; onChange: (v: string) => void; options: string[] }) {
  return (
    <label className="text-xs text-slate-600">
      {props.label}
      <select
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-sm"
      >
        <option value="">(any)</option>
        {props.options.map((o) => (<option key={o} value={o}>{o}</option>))}
      </select>
    </label>
  )
}

function StatusChip({ status }: { status: string }) {
  const failed = status.includes('failed')
  const cls = failed
    ? 'bg-red-100 text-red-800'
    : status === 'completed'
    ? 'bg-emerald-100 text-emerald-800'
    : 'bg-slate-100 text-slate-700'
  return <span className={`rounded px-2 py-0.5 text-xs font-medium ${cls}`}>{status}</span>
}

function StornoBadge({ originalId }: { originalId: string }) {
  return (
    <Link
      to={`/invoices/${originalId}`}
      title={`Storno of ${originalId}`}
      className="ml-2 inline-flex items-center rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-800 hover:bg-red-200"
    >
      <span aria-hidden="true" className="mr-0.5">✕</span>STORNO
    </Link>
  )
}

function TaxStatusChip({ status }: { status: string }) {
  const cls =
    status === 'ACCEPTED' ? 'bg-emerald-100 text-emerald-800'
    : status === 'REJECTED' || status === 'FAILED' ? 'bg-red-100 text-red-800'
    : status === 'SUBMITTED' ? 'bg-blue-100 text-blue-800'
    : 'bg-slate-100 text-slate-700'
  return <span className={`rounded px-2 py-0.5 text-xs font-medium ${cls}`}>{status}</span>
}
